import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  RefreshCw,
  Send,
  Smartphone,
  Users,
  X,
} from 'lucide-react';
import { adminAPI } from '../services/api';

const PLATFORMS = [
  { value: 'all', label: 'All devices', hint: 'iOS + Android' },
  { value: 'ios', label: 'iOS', hint: 'Apple devices' },
  { value: 'android', label: 'Android', hint: 'Google Play' },
];

const cx = (...parts) => parts.filter(Boolean).join(' ');

export default function PushNotifications() {
  const [platform, setPlatform] = useState('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const toastRef = useRef(null);

  const showToast = useCallback((message, kind = 'success') => {
    setToast({ message, kind });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => toastRef.current && clearTimeout(toastRef.current), []);

  const loadPreview = useCallback(async () => {
    try {
      setPreviewLoading(true);
      setError('');
      const res = await adminAPI.previewBroadcastPush({ platform });
      setPreview(res.data.preview);
    } catch (err) {
      setPreview(null);
      setError(err.response?.data?.error || 'Failed to load device count');
    } finally {
      setPreviewLoading(false);
    }
  }, [platform]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const iosCount = preview?.breakdown?.ios ?? 0;
  const androidCount = preview?.breakdown?.android ?? 0;
  const canSend = Boolean(preview?.tokenCount && title.trim() && body.trim() && !sending);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Title and message body are required.');
      return;
    }
    if (!preview?.tokenCount) {
      setError('No devices to send to for this platform.');
      return;
    }

    setShowConfirm(false);
    setSending(true);
    setError('');

    try {
      const res = await adminAPI.sendBroadcastPush({
        platform,
        title: title.trim(),
        body: body.trim(),
      });
      const result = res.data.result;
      showToast(
        `Delivered to ${result.successCount} of ${result.tokenCount} device(s) on ${result.platform}.`,
        result.failureCount ? 'warning' : 'success'
      );
      await loadPreview();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send push notification');
    } finally {
      setSending(false);
    }
  };

  const platformLabel = PLATFORMS.find((p) => p.value === platform)?.label ?? platform;

  return (
    <div className="pn-page">
      <header className="pn-header">
        <div>
          <div className="pn-eyebrow">
            <BellRing size={16} />
            Broadcast
          </div>
          <h1>Push Notifications</h1>
          <p>
            Send a one-time push to active StrapIn users with registered devices.
            Same delivery as the backend broadcast script.
          </p>
        </div>
        <button
          type="button"
          className="pn-btn pn-btn-secondary"
          onClick={loadPreview}
          disabled={previewLoading}
        >
          <RefreshCw size={16} className={previewLoading ? 'pn-spin' : undefined} />
          Refresh counts
        </button>
      </header>

      {error && (
        <div className="pn-alert pn-alert-error" role="alert">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" className="pn-alert-dismiss" onClick={() => setError('')} aria-label="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="pn-stat-row">
        <StatCard
          label="Devices to notify"
          value={previewLoading && !preview ? '—' : (preview?.tokenCount ?? 0)}
          icon={<Smartphone size={18} />}
          tone="primary"
          loading={previewLoading && !preview}
        />
        <StatCard
          label="Users with tokens"
          value={previewLoading && !preview ? '—' : (preview?.usersWithTokens ?? 0)}
          icon={<Users size={18} />}
          tone="neutral"
          loading={previewLoading && !preview}
        />
        <StatCard
          label="iOS devices"
          value={previewLoading && !preview ? '—' : iosCount}
          icon={<span className="pn-platform-icon">🍎</span>}
          tone="info"
          loading={previewLoading && !preview}
        />
        <StatCard
          label="Android devices"
          value={previewLoading && !preview ? '—' : androidCount}
          icon={<span className="pn-platform-icon">🤖</span>}
          tone="success"
          loading={previewLoading && !preview}
        />
      </div>

      <div className="pn-grid">
        <section className="pn-panel">
          <div className="pn-panel-head">
            <h2>Compose notification</h2>
            <span className="pn-badge">{platformLabel}</span>
          </div>

          <div className="pn-field">
            <label>Target platform</label>
            <div className="pn-segment" role="group" aria-label="Target platform">
              {PLATFORMS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={cx('pn-segment-btn', platform === p.value && 'pn-segment-btn-active')}
                  onClick={() => setPlatform(p.value)}
                  aria-pressed={platform === p.value}
                >
                  <span className="pn-segment-label">{p.label}</span>
                  <span className="pn-segment-hint">{p.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pn-field">
            <label htmlFor="push-title">Notification title</label>
            <input
              id="push-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New feature just dropped"
              maxLength={120}
            />
            <span className="pn-char-count">{title.length}/120</span>
          </div>

          <div className="pn-field">
            <label htmlFor="push-body">Notification message</label>
            <textarea
              id="push-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Short description shown on the lock screen and notification tray…"
              rows={5}
              maxLength={500}
            />
            <span className="pn-char-count">{body.length}/500</span>
          </div>

          <div className="pn-summary">
            <div>
              <span className="pn-summary-label">Ready to send</span>
              <strong>{preview?.tokenCount ?? 0} devices</strong>
            </div>
            <p className="pn-summary-note">
              Active, non-suspended users only. Delivery is batched via FCM — allow a few seconds for large audiences.
            </p>
          </div>

          <div className="pn-actions">
            <button
              type="button"
              className="pn-btn pn-btn-primary"
              disabled={!canSend}
              onClick={() => setShowConfirm(true)}
            >
              {sending ? (
                <>
                  <span className="pn-spinner" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send push notification
                </>
              )}
            </button>
            {!preview?.tokenCount && preview && !previewLoading && (
              <p className="pn-hint">No devices registered for this platform.</p>
            )}
          </div>
        </section>

        <aside className="pn-preview-col">
          <div className="pn-preview-sticky">
            <span className="pn-preview-label">Lock screen preview</span>
            <div className="pn-phone">
              <div className="pn-phone-notch" />
              <div className="pn-phone-screen">
                <div className="pn-phone-time">9:41</div>
                <div className="pn-phone-date">Tuesday, June 30</div>
                <div className="pn-push-card">
                  <div className="pn-push-app">
                    <span className="pn-push-icon">S</span>
                    <span>StrapIn</span>
                    <span className="pn-push-time">now</span>
                  </div>
                  <p className="pn-push-title">{title.trim() || 'Notification title'}</p>
                  <p className="pn-push-body">{body.trim() || 'Your message will appear here.'}</p>
                </div>
              </div>
            </div>
            <p className="pn-preview-footnote">
              Preview is approximate. Actual appearance varies by OS version and device settings.
            </p>
          </div>
        </aside>
      </div>

      {showConfirm && (
        <div className="pn-modal-overlay" onClick={() => setShowConfirm(false)}>
          <div
            className="pn-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <div className="pn-modal-header">
              <h3 id="confirm-title">Confirm broadcast</h3>
              <button type="button" className="pn-icon-btn" onClick={() => setShowConfirm(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="pn-modal-body">
              <p>
                Send this notification to <strong>{preview?.tokenCount ?? 0}</strong> device(s) on{' '}
                <strong>{platformLabel}</strong>?
              </p>
              <div className="pn-modal-preview">
                <span className="pn-modal-app">StrapIn</span>
                <strong>{title.trim()}</strong>
                <p>{body.trim()}</p>
              </div>
              <div className="pn-modal-warning">
                <AlertTriangle size={16} />
                This cannot be undone. Users will receive the push immediately.
              </div>
            </div>
            <div className="pn-modal-footer">
              <button type="button" className="pn-btn pn-btn-ghost" onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="pn-btn pn-btn-primary" onClick={handleSend}>
                <Send size={16} />
                Send now
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={cx('pn-toast', toast.kind === 'error' && 'pn-toast-error', toast.kind === 'warning' && 'pn-toast-warning')}>
          {toast.kind === 'error' ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      <style>{STYLES}</style>
    </div>
  );
}

function StatCard({ label, value, icon, tone = 'neutral', loading }) {
  return (
    <div className={cx('pn-stat-card', loading && 'pn-stat-loading')}>
      <div className={cx('pn-stat-icon', `pn-stat-icon-${tone}`)}>{icon}</div>
      <div>
        <div className="pn-stat-label">{label}</div>
        <div className="pn-stat-value">{value}</div>
      </div>
    </div>
  );
}

const STYLES = `
.pn-page {
  --pn-primary: #ec4899;
  --pn-primary-dark: #9333ea;
  --pn-primary-soft: #fdf2f8;
  --pn-success: #12b76a;
  --pn-success-soft: #ecfdf3;
  --pn-info: #2e90fa;
  --pn-info-soft: #eff8ff;
  --pn-warning: #f79009;
  --pn-warning-soft: #fffaeb;
  --pn-danger: #f04438;
  --pn-danger-soft: #fef3f2;
  --pn-neutral-soft: #f2f4f7;
  --pn-text: #101828;
  --pn-text-2: #475467;
  --pn-text-3: #98a2b3;
  --pn-bg: #f7f8fb;
  --pn-surface: #ffffff;
  --pn-border: #e4e7ec;
  --pn-border-strong: #d0d5dd;
  --pn-radius-lg: 14px;
  --pn-radius-md: 10px;
  --pn-radius-sm: 7px;
  --pn-shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.06);
  --pn-shadow-md: 0 8px 24px rgba(16, 24, 40, 0.08);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: var(--pn-bg);
  color: var(--pn-text);
  margin: -32px -24px -24px;
  padding: 28px 32px 64px;
  min-height: calc(100vh - 72px);
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.45;
}
.pn-page * { box-sizing: border-box; }
.pn-page h1, .pn-page h2, .pn-page h3 { margin: 0; font-weight: 600; letter-spacing: -0.01em; color: var(--pn-text); }
.pn-page p { margin: 0; }
.pn-page input, .pn-page textarea, .pn-page button { font-family: inherit; font-size: 13.5px; color: var(--pn-text); }

.pn-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.pn-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--pn-primary);
  margin-bottom: 8px;
}
.pn-header h1 { font-size: 26px; margin-bottom: 8px; }
.pn-header p { color: var(--pn-text-2); max-width: 560px; }

.pn-stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}
.pn-stat-card {
  background: var(--pn-surface);
  border: 1px solid var(--pn-border);
  border-radius: var(--pn-radius-lg);
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: var(--pn-shadow-sm);
}
.pn-stat-loading { opacity: 0.65; }
.pn-stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pn-stat-icon-primary { background: var(--pn-primary-soft); color: var(--pn-primary); }
.pn-stat-icon-neutral { background: var(--pn-neutral-soft); color: var(--pn-text-2); }
.pn-stat-icon-info { background: var(--pn-info-soft); color: var(--pn-info); }
.pn-stat-icon-success { background: var(--pn-success-soft); color: var(--pn-success); }
.pn-stat-label { font-size: 12px; color: var(--pn-text-2); margin-bottom: 2px; }
.pn-stat-value { font-size: 22px; font-weight: 700; letter-spacing: -0.02em; }
.pn-platform-icon { font-size: 18px; line-height: 1; }

.pn-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;
}
.pn-panel {
  background: var(--pn-surface);
  border: 1px solid var(--pn-border);
  border-radius: var(--pn-radius-lg);
  box-shadow: var(--pn-shadow-sm);
  padding: 22px;
}
.pn-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--pn-border);
}
.pn-panel-head h2 { font-size: 16px; }
.pn-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: var(--pn-primary-soft);
  color: #9d174d;
}

.pn-field { display: flex; flex-direction: column; gap: 8px; margin-bottom: 18px; position: relative; }
.pn-field label { font-size: 13px; font-weight: 600; color: var(--pn-text); }
.pn-field input,
.pn-field textarea {
  width: 100%;
  border: 1px solid var(--pn-border);
  border-radius: var(--pn-radius-md);
  padding: 11px 14px;
  background: var(--pn-surface);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  resize: vertical;
}
.pn-field input:focus,
.pn-field textarea:focus {
  border-color: var(--pn-primary);
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.15);
}
.pn-char-count {
  position: absolute;
  right: 0;
  bottom: -18px;
  font-size: 11px;
  color: var(--pn-text-3);
}

.pn-segment {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.pn-segment-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 14px;
  border: 1px solid var(--pn-border);
  border-radius: var(--pn-radius-md);
  background: var(--pn-surface);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  text-align: left;
}
.pn-segment-btn:hover { border-color: var(--pn-border-strong); background: #fafbfc; }
.pn-segment-btn-active {
  border-color: var(--pn-primary);
  background: var(--pn-primary-soft);
  box-shadow: 0 0 0 1px rgba(236, 72, 153, 0.2);
}
.pn-segment-label { font-weight: 600; font-size: 13px; }
.pn-segment-hint { font-size: 11px; color: var(--pn-text-3); }

.pn-summary {
  background: var(--pn-neutral-soft);
  border-radius: var(--pn-radius-md);
  padding: 14px 16px;
  margin-bottom: 20px;
}
.pn-summary-label {
  display: block;
  font-size: 12px;
  color: var(--pn-text-2);
  margin-bottom: 4px;
}
.pn-summary strong { font-size: 18px; }
.pn-summary-note { margin-top: 8px; font-size: 12px; color: var(--pn-text-2); }

.pn-actions { display: flex; flex-direction: column; gap: 8px; }
.pn-hint { font-size: 12px; color: var(--pn-text-2); }

.pn-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: var(--pn-radius-sm);
  border: 1px solid transparent;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.05s;
  white-space: nowrap;
}
.pn-btn:active:not(:disabled) { transform: translateY(1px); }
.pn-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pn-btn-primary {
  background: linear-gradient(135deg, var(--pn-primary), var(--pn-primary-dark));
  color: #fff;
  box-shadow: var(--pn-shadow-sm);
  width: 100%;
  padding: 13px 18px;
  font-size: 14px;
}
.pn-btn-primary:hover:not(:disabled) { box-shadow: 0 8px 20px rgba(236, 72, 153, 0.35); }
.pn-btn-secondary {
  background: var(--pn-surface);
  border-color: var(--pn-border);
  color: var(--pn-text);
}
.pn-btn-secondary:hover:not(:disabled) { border-color: var(--pn-border-strong); background: #fafbfc; }
.pn-btn-ghost { background: transparent; color: var(--pn-text-2); }
.pn-btn-ghost:hover:not(:disabled) { background: var(--pn-neutral-soft); color: var(--pn-text); }

.pn-preview-col { min-width: 0; }
.pn-preview-sticky { position: sticky; top: 88px; }
.pn-preview-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--pn-text-3);
  margin-bottom: 12px;
}
.pn-phone {
  width: 280px;
  margin: 0 auto;
  border-radius: 32px;
  padding: 10px;
  background: linear-gradient(160deg, #1f2937, #111827);
  box-shadow: var(--pn-shadow-md);
}
.pn-phone-notch {
  width: 96px;
  height: 24px;
  background: #000;
  border-radius: 0 0 14px 14px;
  margin: 0 auto 8px;
}
.pn-phone-screen {
  border-radius: 24px;
  min-height: 420px;
  padding: 18px 14px;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
}
.pn-phone-time {
  font-size: 52px;
  font-weight: 300;
  color: #fff;
  letter-spacing: -0.03em;
  line-height: 1;
}
.pn-phone-date {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 4px;
  margin-bottom: 24px;
}
.pn-push-card {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 12px 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
.pn-push-app {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--pn-text-2);
  margin-bottom: 6px;
}
.pn-push-icon {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: linear-gradient(135deg, var(--pn-primary), var(--pn-primary-dark));
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pn-push-time { margin-left: auto; font-size: 11px; color: var(--pn-text-3); }
.pn-push-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--pn-text);
  line-height: 1.3;
}
.pn-push-body {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--pn-text-2);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pn-preview-footnote {
  margin-top: 12px;
  font-size: 11px;
  color: var(--pn-text-3);
  text-align: center;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
}

.pn-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--pn-radius-md);
  margin-bottom: 20px;
  font-size: 13px;
}
.pn-alert-error {
  background: var(--pn-danger-soft);
  border: 1px solid #fecdca;
  color: #b42318;
}
.pn-alert-dismiss {
  margin-left: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  padding: 4px;
  display: inline-flex;
}
.pn-alert-dismiss:hover { opacity: 1; }

.pn-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 100;
}
.pn-modal {
  width: 100%;
  max-width: 440px;
  background: var(--pn-surface);
  border-radius: var(--pn-radius-lg);
  box-shadow: var(--pn-shadow-md);
  overflow: hidden;
}
.pn-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--pn-border);
}
.pn-modal-header h3 { font-size: 16px; }
.pn-modal-body { padding: 18px 20px; display: flex; flex-direction: column; gap: 14px; }
.pn-modal-body p { color: var(--pn-text-2); }
.pn-modal-preview {
  background: var(--pn-neutral-soft);
  border-radius: var(--pn-radius-md);
  padding: 14px;
}
.pn-modal-app {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--pn-text-3);
  margin-bottom: 6px;
}
.pn-modal-preview strong { display: block; margin-bottom: 4px; }
.pn-modal-preview p { font-size: 13px; color: var(--pn-text-2); }
.pn-modal-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #93530c;
  background: var(--pn-warning-soft);
  border-radius: var(--pn-radius-sm);
  padding: 10px 12px;
}
.pn-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--pn-border);
}
.pn-icon-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--pn-radius-sm);
  background: transparent;
  color: var(--pn-text-2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pn-icon-btn:hover { background: var(--pn-neutral-soft); color: var(--pn-text); }

.pn-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 110;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: var(--pn-radius-md);
  background: #067647;
  color: #fff;
  box-shadow: var(--pn-shadow-md);
  max-width: 420px;
  animation: pn-slide-up 0.25s ease;
}
.pn-toast-warning { background: #b54708; }
.pn-toast-error { background: #b42318; }

.pn-spin { animation: pn-spin 0.9s linear infinite; }
.pn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: pn-spin 0.7s linear infinite;
}
@keyframes pn-spin { to { transform: rotate(360deg); } }
@keyframes pn-slide-up {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 1100px) {
  .pn-stat-row { grid-template-columns: repeat(2, 1fr); }
  .pn-grid { grid-template-columns: 1fr; }
  .pn-preview-sticky { position: static; }
  .pn-phone { margin-bottom: 8px; }
}
@media (max-width: 640px) {
  .pn-page { margin: -32px -16px -24px; padding: 20px 16px 48px; }
  .pn-segment { grid-template-columns: 1fr; }
  .pn-stat-row { grid-template-columns: 1fr; }
  .pn-header { flex-direction: column; }
}
`;
